import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { aiServiceConfig } from '@bandos/shared'
import { calculateNodeQualityScore, NODE_QUALITY_CONFIG } from '../../../features/nodeDataQuality'

/**
 * Get combined content from all relevant fields of a node.
 * Used for:
 * - Change detection (detecting significant content changes)
 * - Word count validation (ensuring minimum content before AI generation)
 *
 * Note: companyName is excluded because it's just an identifier - we need
 * substantive content like description to generate meaningful suggestions.
 */
function getNodeContent(node: Node<ReactFlowNodeData>): string {
  const data = node.data
  const parts: string[] = []

  // Common fields
  if (data.label) {
    parts.push(data.label)
  }
  if (data.description && typeof data.description === 'string') {
    parts.push(data.description)
  }

  // Venture fields (companyName excluded - it's just an identifier)
  if (data.businessModelAndValueProp) {
    parts.push(String(data.businessModelAndValueProp))
  }
  if (data.capabilitiesAndAssets) {
    parts.push(String(data.capabilitiesAndAssets))
  }
  if (data.hardConstraints) {
    parts.push(String(data.hardConstraints))
  }
  if (data.goals && typeof data.goals === 'string') {
    parts.push(data.goals)
  }

  return parts.join(' ')
}

/**
 * Count words in a string.
 * Words are split on whitespace and filtered for non-empty strings.
 */
function countWords(content: string): number {
  return content.split(/\s+/).filter((w) => w.length > 0).length
}

/**
 * Check if a node has sufficient content for AI generation.
 * Uses MIN_WORD_COUNT from shared config.
 *
 * @returns true if node has enough words across all fields
 */
function hasMinimumContent(node: Node<ReactFlowNodeData>): boolean {
  const content = getNodeContent(node)
  const wordCount = countWords(content)
  return wordCount >= aiServiceConfig.MIN_WORD_COUNT
}

/**
 * Check if a node meets the quality score threshold for AI generation.
 * For generatable node types (venture, idea), uses the per-field
 * weighted scoring system defined in nodeDataQuality/config.ts.
 * Falls back to the legacy word-count check for all other node types.
 */
export function hasMinimumQualityScore(
  node: Node<ReactFlowNodeData>,
  qualityGateEnabled: boolean = true
): boolean {
  if (!qualityGateEnabled) return true
  const category = node.data.category as string
  if (!NODE_QUALITY_CONFIG[category]) {
    return hasMinimumContent(node)
  }
  return calculateNodeQualityScore(node.data).meetsAIThreshold
}
